import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Spin, Alert, Tabs, Card } from 'antd';
import { TrophyOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { apiClient } from '../services/api';
import BrandCard from '../components/BrandCard';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
// 使用 antd v5 的 Tabs items 写法，避免 TabPane 弃用告警

interface RecommendedBrand {
  id: number;
  name: string;
  description: string;
  founded: string;
  headquarters: string;
  website: string;
  categories: string[];
  socialMedia: any;
  recommendationScore?: number;
  recommendationReasons?: string[];
}

const RecommendationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();

  const [personalRecommendations, setPersonalRecommendations] = useState<
    RecommendedBrand[]
  >([]);
  const [trendingBrands, setTrendingBrands] = useState<RecommendedBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRecommendations();
  }, [isAuthenticated]);

  const loadRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      // 加载热门品牌
      const trendingResponse = await apiClient.getTrendingBrands(6);
      setTrendingBrands(trendingResponse.data);

      // 如果用户已登录，加载个性化推荐
      if (isAuthenticated) {
        try {
          const personalResponse =
            await apiClient.getPersonalRecommendations(6);
          setPersonalRecommendations(personalResponse.data);
        } catch (_personalError) {
          // 个性化推荐失败不影响整体页面，静默忽略
        }
      }
    } catch (err: any) {
      setError(err.message || '加载推荐失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (id: number) => {
    navigate(`/brands/${id}`);
  };

  const RecommendationCard: React.FC<{ brand: RecommendedBrand }> = ({
    brand,
  }) => (
    <Card
      hoverable
      onClick={() => handleViewDetails(brand.id)}
      style={{ marginBottom: 16, cursor: 'pointer' }}
      actions={[
        <span
          key="view"
          onClick={() => handleViewDetails(brand.id)}
          style={{ cursor: 'pointer', color: '#1890ff' }}
        >
          {t('recommendations.view_details')}
        </span>,
      ]}
    >
      <Card.Meta
        title={brand.name}
        description={
          <div>
            <p style={{ marginBottom: 8 }}>{brand.description}</p>
            <p style={{ marginBottom: 8, color: '#666' }}>
              {t('recommendations.founded_short')}:{' '}
              {new Date(brand.founded).getFullYear()} |{' '}
              {t('recommendations.headquarters_short')}: {brand.headquarters}
            </p>
            {brand.recommendationReasons &&
              brand.recommendationReasons.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: '#52c41a' }}>
                    {t('recommendations.reasons_title')}
                  </strong>
                  <div style={{ marginTop: 4 }}>
                    {brand.recommendationReasons.map((reason, index) => (
                      <span
                        key={index}
                        style={{
                          display: 'inline-block',
                          background: '#f6ffed',
                          color: '#52c41a',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginRight: '4px',
                          marginBottom: '4px',
                        }}
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        }
      />
    </Card>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>{t('recommendations.title')}</Title>

      {error && (
        <Alert
          message={t('recommendations.load_failed')}
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs
        defaultActiveKey={isAuthenticated ? 'personal' : 'trending'}
        items={[
          ...(isAuthenticated
            ? [
                {
                  key: 'personal',
                  label: (
                    <span>
                      <UserOutlined />
                      {t('recommendations.tab_personal')}
                    </span>
                  ),
                  children: (
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <p style={{ color: '#666' }}>
                          {t('recommendations.personal_desc')}
                        </p>
                      </div>
                      {personalRecommendations.length > 0 ? (
                        <Row gutter={[16, 16]}>
                          {personalRecommendations.map(brand => (
                            <Col span={8} key={brand.id}>
                              <RecommendationCard brand={brand} />
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '48px' }}>
                          <p>{t('recommendations.personal_empty')}</p>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          {
            key: 'trending',
            label: (
              <span>
                <TrophyOutlined />
                {t('recommendations.tab_trending')}
              </span>
            ),
            children: (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <p style={{ color: '#666' }}>
                    {t('recommendations.trending_desc')}
                  </p>
                </div>
                {trendingBrands.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {trendingBrands.map(brand => (
                      <Col span={8} key={brand.id}>
                        <BrandCard
                          brand={brand as any}
                          onViewDetails={handleViewDetails}
                        />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px' }}>
                    <p>{t('recommendations.trending_empty')}</p>
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default RecommendationsPage;
